import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { approveSelfRegistrationRequest, denySelfRegistrationRequest, fetchSelfRegistrationRequests } from "./api";
import { useAuth } from "../auth/useAuth";
import type { SelfRegistrationListPage } from "./types";

const APPROVALS_QUERY_KEY = (status: string) => ["self-registration", "requests", status] as const;

export const useSelfRegistrationRequests = (
  status: "pending" | "approved" | "denied" = "pending",
  pageSize: number = 25,
  page: number = 0
) =>
  useQuery<SelfRegistrationListPage>({
    queryKey: [...APPROVALS_QUERY_KEY(status), "page", pageSize, page],
    queryFn: () => fetchSelfRegistrationRequests(status, { limit: pageSize, offset: page * pageSize }),
    refetchInterval: status === "pending" ? 15000 : false
  });

export const useApproveSelfRegistration = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => approveSelfRegistrationRequest(requestId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["self-registration", "requests"] });
    }
  });
};

export const useDenySelfRegistration = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (args: { requestId: string; message_to_requester?: string; decision_reason?: string }) =>
      denySelfRegistrationRequest(args.requestId, {
        message_to_requester: args.message_to_requester,
        decision_reason: args.decision_reason
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["self-registration", "requests"] });
    }
  });
};


/**
 * Number of pending self-registration requests, for the header badge. Uses the list
 * endpoint with `limit=1` and reads `total`. Only runs for roles that can review requests.
 */
export const usePendingApprovalsCount = () => {
  const { user } = useAuth();
  const canReview = user?.role === "superuser" || user?.role === "retailer";
  return useQuery<SelfRegistrationListPage, Error, number>({
    queryKey: [...APPROVALS_QUERY_KEY("pending"), "count"],
    queryFn: () => fetchSelfRegistrationRequests("pending", { limit: 1, offset: 0 }),
    select: (d) => d.total,
    refetchInterval: 60_000,
    enabled: canReview
  });
};
